/* Purpose: measure shared header geometry without replacing Impulse's sticky,
 * drawer, search, localization, announcement or geo/countdown controllers.
 * Usage: loaded once by mvk-header-loader; section flags/settings opt in.
 * Scope: dimensions and overflow fallback only. No network or commerce actions. */
(() => {
  if (window.mvkHeaderChromeLoaded) return;
  window.mvkHeaderChromeLoaded = true;
  let cleanup = () => {};
  function mount() {
    cleanup();
    const root = document.querySelector('[data-mvk-header]');
    const html = document.documentElement;
    if (!root) return;
    const header = root.querySelector('#SiteHeader'), bar = root.querySelector('.announcement-bar');
    const wrapper = root.querySelector('#HeaderWrapper');
    if (!header) return;
    const layout = header.querySelector('.header-layout'), nav = header.querySelector('.header-item--navigation');
    const logo = header.querySelector('.header-item--logo'), icons = header.querySelector('.header-item--icons');
    let frame, disposed = false;
    const set = (key, value) => { if (html.style.getPropertyValue(key) !== value) html.style.setProperty(key, value); };
    function restoreTopOverlay() {
      // The native sticky/hover controller can leave a white inline background
      // after scrolling home. Restore only the Home hero's opted-in idle state;
      // drawers, search and focused menus retain their native solid surface.
      const eligible = document.body.classList.contains('template-index') &&
          header.dataset.overlay === 'true' && !!wrapper &&
          (innerWidth > 768 || root.dataset.mvkMobileOverlay === 'true') &&
          scrollY <= 1 && !document.body.classList.contains('js-drawer-open') &&
          !header.matches(':hover') &&
          !root.querySelector('.site-nav__item.is-focused, .site-header__search-container.is-active');
      html.classList.toggle('mvk-header-home-top', eligible);
      if (eligible && !header.classList.contains('site-header--stuck')) {
        // Avoid notifying our wrapper MutationObserver with an unchanged class.
        // Rewriting it every measure schedules another frame indefinitely.
        if (!wrapper.classList.contains('is-light')) wrapper.classList.add('is-light');
        if (wrapper.style.backgroundColor) wrapper.style.removeProperty('background-color');
      }
    }
    function measure() {
      if (disposed) return;
      restoreTopOverlay();
      const pinned = root.dataset.mvkPinAnnouncement === 'true';
      // Pin-off still uses native sticky announcements after their threshold.
      const barFixed = bar && getComputedStyle(bar).position === 'fixed';
      const barHeight = bar && (pinned || barFixed) ? bar.getBoundingClientRect().height : 0;
      set('--mvk-announcement-height', barHeight + 'px');
      set('--mvk-sticky-header-height', header.getBoundingClientRect().height + 'px');
      root.classList.add('mvk-header-measured');
      // Match the native controller's actual position, including its short-screen
      // non-sticky fallback, rather than inferring fixed state from scroll distance.
      html.classList.toggle('mvk-header-stuck', getComputedStyle(header).position === 'fixed');
      // Update the native flow spacer after fonts or editor settings change.
      const spacer = header.parentElement;
      if (spacer.id === 'StickyHeaderWrap' && spacer.style.height !== header.offsetHeight + 'px') spacer.style.height = header.offsetHeight + 'px';
      // Fit only left-logo layouts, where the native icon snippet provides a drawer
      // button. Existing alternative alignment options remain controlled by Impulse.
      if (nav && logo && icons && layout?.dataset.logoAlign === 'left' && innerWidth > 768) {
        const menu = nav.querySelector('.site-nav');
        if (menu) {
          const required = [...menu.children].reduce((sum, el) => sum + el.getBoundingClientRect().width, 0);
          // Exclude the overflow drawer button from the fit budget to avoid a loop
          // where opening the fallback itself makes a formerly fitting menu fail.
          const iconWidth = [...icons.querySelectorAll('.site-nav__link')].filter(el => !el.classList.contains('js-drawer-open-nav')).reduce((sum, el) => sum + el.getBoundingClientRect().width, 0);
          root.classList.toggle('mvk-header-menu-overflow', required + logo.getBoundingClientRect().width + iconWidth + 24 > layout.clientWidth);
        }
      } else root.classList.remove('mvk-header-menu-overflow');
    }
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    // Size changes come from actual content and fonts. Class observation follows
    // the existing sticky controller without adding another scroll listener.
    const resize = new ResizeObserver(schedule);
    [bar, header, layout].filter(Boolean).forEach(el => resize.observe(el));
    const state = new MutationObserver(schedule);
    state.observe(header, {attributes: true, attributeFilter: ['class']});
    if (wrapper) state.observe(wrapper, {attributes: true, attributeFilter: ['class', 'style']});
    state.observe(document.body, {attributes: true, attributeFilter: ['class']});
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, {passive:true});
    header.addEventListener('mouseenter', schedule);
    header.addEventListener('mouseleave', schedule);
    document.fonts?.ready.then(() => { if (!disposed) schedule(); });
    measure();
    cleanup = () => {
      disposed = true; cancelAnimationFrame(frame); resize.disconnect(); state.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule);
      header.removeEventListener('mouseenter', schedule);
      header.removeEventListener('mouseleave', schedule);
      root.classList.remove('mvk-header-measured', 'mvk-header-menu-overflow');
      html.classList.remove('mvk-header-stuck');
      html.classList.remove('mvk-header-home-top');
      html.style.removeProperty('--mvk-announcement-height'); html.style.removeProperty('--mvk-sticky-header-height');
    };
  }
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.querySelector?.('[data-mvk-header]')) cleanup();
  });
  mount();
})();
